import { z } from 'zod';
import type { ReportAnswers, ReportFactsDto, ScoreLine, TeacherReportStatus, VoiceNoteDto } from './teacher-report-scoring.js';
import type { ReportKind, ReportMode, ReportTemplateDto } from './teacher-reports.js';

/**
 * Teacher reports — review queue, points summary and leaderboard shapes
 * (Phase 4 of docs/teacher-reports-implementation-plan.md).
 */

export const reportApproveSchema = z
  .object({
    qualityBonus: z.number().int().min(0).max(50).default(0),
    reversals: z
      .array(z.object({ itemKey: z.string().min(1).max(48), reason: z.string().trim().min(3).max(300) }).strict())
      .max(30)
      .default([]),
  })
  .strict();
export type ReportApproveInput = z.infer<typeof reportApproveSchema>;

export const reportBulkApproveSchema = z.object({ ids: z.array(z.string().uuid()).min(1).max(50) }).strict();

export const reportRequestChangesSchema = z.object({ note: z.string().trim().min(3).max(1000) }).strict();

export const reportReverseItemSchema = z
  .object({ itemKey: z.string().min(1).max(48), reason: z.string().trim().min(3).max(300) })
  .strict();

export const reportExcuseSchema = z.object({ reason: z.string().trim().min(3).max(500) }).strict();

export const reviewQueueQuerySchema = z
  .object({
    status: z.enum(['submitted', 'changes_requested', 'approved', 'missed']).default('submitted'),
    flagged: z.enum(['true', 'false']).optional(),
    batchId: z.string().uuid().optional(),
    teacherId: z.string().uuid().optional(),
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/u).optional(),
  })
  .strict();

export interface ReportFlagDto {
  code: string;
  message: string;
  resolved: boolean;
}

export interface ReviewQueueItem {
  id: string;
  teacherId: string;
  teacherName: string;
  batchId: string;
  batchName: string;
  kind: ReportKind;
  mode: ReportMode;
  periodKey: string;
  periodStart: string;
  status: TeacherReportStatus;
  isLate: boolean;
  pointsTotal: number | null;
  submittedAt: string | null;
  flags: ReportFlagDto[];
}

export interface ReviewReportDetail extends ReviewQueueItem {
  voiceNote: VoiceNoteDto | null;
  answers: ReportAnswers;
  facts: ReportFactsDto | null;
  pointsLines: ScoreLine[] | null;
  template: ReportTemplateDto;
  reviewNote: string | null;
  /** Items already reversed (after approval). */
  reversedItems: string[];
  qualityBonus: number;
  maxQualityBonus: number;
}

export interface PointsLedgerEntryDto {
  id: string;
  createdAt: string;
  sourceType: 'report' | 'streak_bonus' | 'perfect_month' | 'quality_bonus' | 'reversal' | 'adjustment';
  points: number;
  status: 'pending' | 'banked' | 'void';
  reason: string | null;
  report: { kind: ReportKind; batchName: string; periodKey: string; periodStart: string } | null;
}

export type PointsDayStatus = 'ontime' | 'late' | 'missed' | 'open' | 'leave' | 'holiday' | 'off' | 'future';

export interface PointsBadgeDto {
  key: 'early_bird' | 'full_week' | 'istiqamah' | 'perfect_month';
  label: string;
  description: string;
  earned: boolean;
  progress: string | null;
}

export interface MyPointsResponse {
  month: string;
  banked: number;
  pending: number;
  /** Paise, from banked points at the current rate, capped. */
  incentivePaise: number;
  ratePaise: number;
  capPaise: number;
  streak: { current: number; length: number; bonus: number; nextBonusIn: number };
  calendar: Array<{ date: string; status: PointsDayStatus }>;
  badges: PointsBadgeDto[];
  ledger: PointsLedgerEntryDto[];
}

export interface LeaderboardRow {
  teacherId: string;
  teacherName: string;
  batchNames: string[];
  banked: number;
  pending: number;
  incentivePaise: number;
  submitted: number;
  onTime: number;
  missed: number;
  onTimePct: number | null;
  flagged: number;
  previousBanked: number;
}

export interface LeaderboardResponse {
  month: string;
  rows: LeaderboardRow[];
  needsAttention: Array<{ teacherId: string; teacherName: string; reason: string }>;
}
